import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search, ArrowRight } from 'lucide-react';
import logo from '@/assets/biolyn-logo.png';

const navLinks = [
  { label: 'Science', href: '#science' },
  { label: 'Products', href: '/products', isRoute: true },
  { label: 'Quality', href: '#quality' },
  { label: 'About', href: '#about' },
  { label: 'Contact', href: '#rfq' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavClick = (link) => {
    setMenuOpen(false);
    if (link.isRoute) {
      navigate(link.href);
      return;
    }
    if (location.pathname !== '/') {
      window.location.href = '/' + link.href;
      return;
    }
    const el = document.querySelector(link.href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-all duration-200 ${
        scrolled
          ? 'shadow-[0_1px_0_rgba(10,17,40,0.06),0_10px_30px_-16px_rgba(10,17,40,0.25)]'
          : 'border-b border-[#EEF1F3]'
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
          <img src={logo} alt="Biolyn Pharmaceutical B.V." className="h-11 w-auto" />
          <span
            className="hidden lg:inline-block text-[10px] tracking-[0.28em] uppercase text-[#6C757D] border-l border-[#DEE2E6] pl-3"
            style={{ fontFamily: 'var(--font-mono)' }}
          >
            Pharmaceutical B.V.
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNavClick(link)}
              className="relative text-sm text-[#1A2233] hover:text-[#0A7C84] transition-colors duration-150 after:absolute after:left-0 after:-bottom-2 after:h-[2px] after:w-0 after:bg-[#0A7C84] hover:after:w-full after:transition-all after:duration-200"
              style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}
              data-testid={`nav-${link.label.toLowerCase()}`}
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* Right actions */}
        <div className="hidden md:flex items-center gap-4">
          <button
            onClick={() => navigate('/products')}
            className="p-2 text-[#1A2233] hover:text-[#0A7C84] transition-colors duration-150"
            aria-label="Search products"
            data-testid="nav-search"
          >
            <Search className="w-[18px] h-[18px]" strokeWidth={1.75} />
          </button>
          <button
            onClick={() => handleNavClick({ href: '#rfq' })}
            className="btn-pill group flex items-center gap-2 pl-5 pr-4 py-2.5 bg-[#0A7C84] text-white text-sm hover:bg-[#075E64]"
            style={{ fontFamily: 'var(--font-body)', fontWeight: 600 }}
            data-testid="nav-request-quote"
          >
            Request a Quote
            <ArrowRight className="w-4 h-4 transition-transform duration-150 group-hover:translate-x-0.5" strokeWidth={2} />
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 text-[#0A1128]"
          onClick={() => setMenuOpen(!menuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {menuOpen ? <X className="w-6 h-6" strokeWidth={1.75} /> : <Menu className="w-6 h-6" strokeWidth={1.75} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-[#EEF1F3] px-6 py-4" data-testid="mobile-menu">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNavClick(link)}
              className="block w-full text-left py-3.5 text-base text-[#1A2233] hover:text-[#0A7C84] border-b border-[#F1F3F5]"
              style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={() => handleNavClick({ href: '#rfq' })}
            className="btn-pill mt-5 w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#0A7C84] text-white text-sm hover:bg-[#075E64]"
            style={{ fontFamily: 'var(--font-body)', fontWeight: 600 }}
          >
            Request a Quote
          </button>
        </div>
      )}
    </header>
  );
}
